import * as React from 'react'

import { Breadcrumbs } from '@user/components/DetailPages/Breadcrumbs'
import { ClosingRequirements } from './sections/ClosingRequirements'
import { DetailPageHeader } from '@user/components/DetailPages/Header'
import { ENTITY_TYPES } from '@sales-center/common'
import type { IOpportunityDetail } from '@user/store/opportunity/typings'
import { Contacts } from '@user/components/DetailPages/sections/Contacts'
import { Notes } from '../../DetailPages/sections/Notes'
import { OpportunityActionsHeader } from './headers/OpportunityActionsHeader'
import { OpportunityProgress } from '@user/components/DetailPages/sections/OpportunityProgress'

import { SoldServicesOppHeader } from '@user/components/SoldServices/SoldServicesOppHeader/SoldServicesOppHeader'

import AppBar from '@mui/material/AppBar'
import { SchedulingWizardContextProvider } from '@user/components/Scheduling'
import {
  useData as useDataActivities,
  useFetchAllEntityActivities,
} from '@user/features/Activity/components/ActivitiesContent/hooks'
import {
  EntityDetailsAdditionalInformation,
  EntityDetailsContentWrapperStyled,
} from '@user/features/EntityDetails/components'

import { CompanyIdentifier } from '@user/features/CompanyIdentifier'
import { useData } from './hooks'
import { EntityDatesPanel } from '@user/features/EntityDetails/components/EntityDatesPanel'
import { ActivityTimelineCard } from '@user/components/DetailPages/sections/ActivityTimeline/ActivityTimelineCard/ActivityTimelineCard'
import { CWQDialogContextProvider } from '@user/components/SoldServices'
import Stack from '@mui/material/Stack'
import Hidden from '@mui/material/Hidden'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import LocationContextProvider from '@user/components/EntityCards/Locations/LocationsPopoverButton/context/LocationContextProvider'
import { Measure } from '@user/components/DetailPages/sections/EntityName/MeasureAI/Measure'
import useMediaQuery from '@mui/material/useMediaQuery'
import { usePropertyDetailsEnabled } from '@user/components/DetailPages/sections/EntityName/MeasureAI/hooks/usePropertyDetailsEnabled'
import {
  getMostRecentKnockStatusFromLocations,
  MostRecentKnockStatus,
} from '@user/features/MapView/MostRecentKnockStatus'

interface Props {
  detail: IOpportunityDetail
  canUpdateOpps: boolean
  canDeleteOpps: boolean
}

export function OpportunityDetailPageContent(props: Props) {
  const { detail, canUpdateOpps, canDeleteOpps } = props

  const {
    status,
    entityName,
    disableEditName,
    entityContactName,
    opportunityMatched,

    funnelStages,

    addContactOpen,
    onOpenAddContact,
    onCloseAddContact,
    hasScheduledAppointment,
  } = useData({
    detail,
    canUpdateOpps,
  })

  const { Content } = useDataActivities()

  const isMobile = useMediaQuery('(max-width:900px)')
  const isSmallScreen = useMediaQuery('(max-width:1125px)')

  useFetchAllEntityActivities(detail.id, ENTITY_TYPES.Opportunity)
  const measureAIEnabled = usePropertyDetailsEnabled()
  const recentKnockStatus = getMostRecentKnockStatusFromLocations(
    detail.locations.serviceLocations,
  )

  const theme = useTheme()
  return (
    <CWQDialogContextProvider>
      <SchedulingWizardContextProvider
        entityId={detail.id}
        entityType={ENTITY_TYPES.Opportunity}
        salesFunnelId={detail.salesFunnelId}
      >
        <LocationContextProvider
          entityId={detail.id}
          entityType={ENTITY_TYPES.Opportunity}
          locations={detail.locations.serviceLocations || []}
        >
          <AppBar position="sticky" color="transparent" elevation={0}>
            <CompanyIdentifier variant="detail" />
            <Breadcrumbs
              entityName={entityName}
              entityType={ENTITY_TYPES.Opportunity}
            />
            <OpportunityActionsHeader
              detail={detail}
              status={status}
              canUpdate={canUpdateOpps}
              canDelete={canDeleteOpps}
              soldServiceStatus={detail.soldServiceStatus}
            />
            {detail.closedDate && (
              <SoldServicesOppHeader detail={detail} status={status} />
            )}
          </AppBar>

          <Stack
            direction="row"
            alignItems="start"
            marginX="auto"
            maxWidth={1368}
            overflow="auto"
          >
            <EntityDetailsContentWrapperStyled>
              <Hidden lgUp>
                <Stack m={2} mb={0}>
                  <EntityDatesPanel
                    detail={detail}
                    canUpdate={canUpdateOpps}
                    canDelete={canDeleteOpps}
                    canConvert={false}
                  />
                  <MostRecentKnockStatus
                    knockStatus={recentKnockStatus}
                    sx={{ mt: 1 }}
                  />
                </Stack>
              </Hidden>
              <DetailPageHeader
                color={theme.palette.common.white}
                companyName={detail.company}
                detail={detail}
                disableEditName={disableEditName}
                entityName={entityContactName}
                entityStatus={status}
                entityType={ENTITY_TYPES.Opportunity}
                initialValue={detail.initialValue}
                recurringValue={detail.recurringValue}
                totalValue={detail.totalLeadValue}
                canUpdate={canUpdateOpps}
                hasScheduledAppointment={!!hasScheduledAppointment}
                mt={isSmallScreen && recentKnockStatus ? 1 : 2}
              />
              {isMobile && measureAIEnabled && (
                <Stack m={2} mt={0}>
                  <Measure
                    entityId={detail.id}
                    entityType={ENTITY_TYPES.Opportunity}
                    variant="location"
                  />
                </Stack>
              )}
              <Stack m={2} mt={0}>
                <Paper elevation={0} variant="outlined">
                  <OpportunityProgress
                    detail={detail}
                    salesFunnelStages={funnelStages}
                    status={status}
                    disabled={!canUpdateOpps}
                  />
                </Paper>
              </Stack>
              <Stack m={2} mt={0}>
                <Paper elevation={0} variant="outlined">
                  <Stack>
                    <Stack p={2} pb={0}>
                      <Typography
                        color={theme.palette.text.primary}
                        mb={2.2}
                        fontSize="20px"
                        fontWeight={500}
                        fontFamily="Source Sans Pro SemiBold"
                      >
                        Closing Activities
                      </Typography>
                    </Stack>
                    <Box mb={1}>
                      <ClosingRequirements
                        detail={detail}
                        canUpdate={canUpdateOpps}
                        scheduleSettings={detail.scheduleSettings}
                      />
                    </Box>
                  </Stack>
                </Paper>
              </Stack>
              <Stack m={2} mt={0}>
                <Paper elevation={0} variant="outlined">
                  <Stack>
                    <Typography
                      variant="h6"
                      color={theme.palette.text.primary}
                      p={2}
                    >
                      Additional Information
                    </Typography>
                    <EntityDetailsAdditionalInformation
                      entityId={detail.id}
                      entityType={ENTITY_TYPES.Opportunity}
                      detail={detail}
                      opportunityStatus={status}
                      canUpdate={canUpdateOpps}
                      entityMatched={opportunityMatched}
                      salesTeamId={detail.salesTeamId.toString()}
                      leadCreatedTime={detail.leadCreatedTime}
                    />
                  </Stack>
                </Paper>
              </Stack>
              <Stack m={2} mt={0}>
                <Paper elevation={0} variant="outlined">
                  <Stack>
                    <Contacts
                      detail={detail}
                      entityId={detail.id}
                      entityType={ENTITY_TYPES.Opportunity}
                      canUpdateEntity={canUpdateOpps}
                      addContactOpen={addContactOpen}
                      onCloseAddContact={onCloseAddContact}
                      onOpenAddContact={onOpenAddContact}
                    />
                  </Stack>
                </Paper>
              </Stack>
              <Stack m={2} mt={0}>
                <Paper elevation={0} variant="outlined">
                  <Notes
                    entityType={ENTITY_TYPES.Opportunity}
                    detail={detail}
                    canUpdate={canUpdateOpps}
                  />
                </Paper>
              </Stack>
              {!!detail && (
                <Stack m={2} mt={0}>
                  <Paper elevation={0} variant="outlined">
                    <ActivityTimelineCard
                      entityId={detail.id}
                      entityType={ENTITY_TYPES.Opportunity}
                      servicesCount={detail.closingRequirements.servicesCount}
                      agreementsCount={
                        detail.closingRequirements.agreementsCount
                      }
                      paymentsCount={detail.closingRequirements.paymentsCount}
                      appointments={detail.closingRequirements.appointments}
                      salesFunnelStageId={detail.salesFunnelStageId}
                      assigneeId={detail.assigneeId}
                    />
                  </Paper>
                </Stack>
              )}
            </EntityDetailsContentWrapperStyled>
            {detail && (
              <>
                <Stack mt="20px!important" mr={2} spacing={1.5}>
                  <Content
                    entityId={detail.id}
                    entityType={ENTITY_TYPES.Opportunity}
                    salesTeamId={detail.salesTeamId}
                    ownerId={detail.assigneeId}
                  />
                  <Hidden mdDown>
                    <MostRecentKnockStatus knockStatus={recentKnockStatus} />
                  </Hidden>
                  {!isMobile && measureAIEnabled && (
                    <Measure
                      entityId={detail.id}
                      entityType={ENTITY_TYPES.Opportunity}
                      variant="detail"
                    />
                  )}
                </Stack>
              </>
            )}
          </Stack>
        </LocationContextProvider>
      </SchedulingWizardContextProvider>
    </CWQDialogContextProvider>
  )
}
